import type { ReactElement } from "react";
import { Badge, Checkbox, Switch } from "@playbookai/ui";
import type {
  ConnectorManifest,
  IntegrationAccessPolicy,
} from "@playbookai/api-clients";
import {
  capabilityInputAllowlist,
  isCapabilityEnabled,
  isProductEnabled,
  productModels,
  probeResourceFor,
  resourceAllowlist,
  setCapabilityInputAllowlist,
  setResourceAllowlistIds,
  toggleCapability,
  toggleProduct,
  type ProductModel,
} from "./manifest-render.js";
import { PolicyAllowlistPicker } from "./PolicyAllowlistPicker.js";
import type { PolicyDiscoveryState } from "./usePolicyDiscovery.js";
import type { AllowlistOption } from "./PolicyAllowlistPicker.js";

/** Options for one resource type, discovered over the given connection. */
export type ResourceOptionsLoader = (
  connectionId: string,
  resourceType: string,
) => Promise<readonly AllowlistOption[]>;

/** Options for one constrainable input field, discovered per resource. */
export type CapabilityInputOptionsLoader = (
  connectionId: string,
  capabilityKey: string,
  resourceId: string,
  field: string,
) => Promise<readonly AllowlistOption[]>;

interface AccessPolicyEditorProps {
  readonly manifest: ConnectorManifest;
  readonly policy: IntegrationAccessPolicy;
  readonly onChange: (policy: IntegrationAccessPolicy) => void;
  /**
   * Where the pickers discover from (PB-4734, G9), and DELIBERATELY PROPS
   * rather than hooks called here.
   *
   * This component is the fictional-provider test's subject: it must render any
   * manifest with no provider knowledge and no environment. Reaching for a
   * query client and an API config inside it would make that test need a
   * provider tree to say something about manifests, and every future renderer
   * of a policy would inherit the dependency.
   *
   * Both default to the honest not-connected case, which is also what the
   * CREATE form wants: there is no instance to connect to yet.
   */
  readonly discovery?: PolicyDiscoveryState | undefined;
  readonly loadResources?: ResourceOptionsLoader | undefined;
  readonly loadCapabilityInputs?: CapabilityInputOptionsLoader | undefined;
  /** Cache identity only — never used to fetch anything from here. */
  readonly tenantIntegrationId?: string | undefined;
}

/**
 * The access-policy editor, generated from the manifest.
 *
 * Products, their capabilities and their resource types are all read from the
 * manifest, including the nesting depth — a provider with three resource levels
 * indents three levels here without any change to this file.
 *
 * Capabilities of a disabled product are rendered but not operable, rather than
 * hidden. An administrator asking "why can't I enable this?" should be able to
 * see the answer; a hidden control just looks like a missing feature.
 */
export function AccessPolicyEditor({
  manifest,
  policy,
  onChange,
  discovery = { kind: "not-connected" },
  loadResources,
  loadCapabilityInputs,
  tenantIntegrationId,
}: AccessPolicyEditorProps): ReactElement {
  const products = productModels(manifest);

  return (
    <div className="space-y-6">
      {products.map((product) => (
        <ProductSection
          key={product.key}
          product={product}
          policy={policy}
          onChange={onChange}
          discovery={discovery}
          loadResources={loadResources}
          loadCapabilityInputs={loadCapabilityInputs}
          tenantIntegrationId={tenantIntegrationId}
        />
      ))}
    </div>
  );
}

function ProductSection({
  product,
  policy,
  onChange,
  discovery,
  loadResources,
  loadCapabilityInputs,
  tenantIntegrationId,
}: {
  product: ProductModel;
  policy: IntegrationAccessPolicy;
  onChange: (policy: IntegrationAccessPolicy) => void;
  discovery: PolicyDiscoveryState;
  loadResources: ResourceOptionsLoader | undefined;
  loadCapabilityInputs: CapabilityInputOptionsLoader | undefined;
  tenantIntegrationId: string | undefined;
}): ReactElement {
  const enabled = isProductEnabled(policy, product.key);

  return (
    <section className="rounded-lg border border-pb-border p-4 space-y-4">
      <header className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold">{product.label}</h4>
        {/* "Allow", not "Enable". This switch decides whether the integration
            is PERMITTED to reach this product; the switch on the integration's
            own row decides whether the integration runs at all. Both used to
            say "Enable <name>", which is how an administrator came to set this
            one and then find their new integration switched off — two
            identical-looking controls, one verb, two unrelated meanings. */}
        <Switch
          checked={enabled}
          onChange={(next) => onChange(toggleProduct(policy, product, next))}
          aria-label={`Allow this integration to use ${product.label}`}
        />
      </header>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-pb-text-muted">
          Capabilities
        </p>
        {product.capabilities.map((capability) => (
          <div key={capability.key} className="flex items-center gap-2">
            <Checkbox
              checked={isCapabilityEnabled(policy, capability.key)}
              disabled={!enabled}
              onChange={(next) =>
                onChange(toggleCapability(policy, capability.key, next))
              }
              label={capability.label}
            />
            <Badge variant="outline">{capability.kind}</Badge>
            {capability.requiresApproval ? (
              // Surfaced because it changes what the administrator is agreeing
              // to: a write capability always needs a human approval at
              // execution time, and that is a property of the capability, not
              // a setting anyone here can turn off.
              <Badge variant="outline">approval required</Badge>
            ) : null}
          </div>
        ))}
        {/* PB-4734 (G9), the input half. `resources` says WHERE an action may
            land; these say WHAT it may create there. Rendered under the
            capability that declares them, because the restriction is scoped to
            that capability and nowhere else. */}
        {product.capabilities.flatMap((capability) => {
          // Computed ONCE per capability. Three call sites re-derived it — the
          // cache key, the loader and the message — and a value that a render
          // reads three times is a value that can be read inconsistently.
          const probeResource = probeResourceFor(policy, capability);
          return capability.constrainableInputs.map((field) => (
            <div
              key={`${capability.key}:${field}`}
              className="ml-6 border-l border-pb-border pl-3"
            >
              {probeResource === null ? (
                // The DEPENDENCY, in place of the picker rather than beneath
                // it. Rendering both showed an empty control and then explained
                // why it was empty, which reads as a broken picker with an
                // apology attached. The resource allowlists are in the section
                // BELOW this one.
                <div className="space-y-1">
                  <p className="block text-xs font-medium">
                    {`${capability.label} — allowed ${field}`}
                  </p>
                  <p className="text-xs text-pb-text-muted">
                    Allow at least one {capability.resourceTypeLabel} below,
                    then these can be listed.
                  </p>
                </div>
              ) : (
                <PolicyAllowlistPicker
                  id={`input-${capability.key}-${field}`}
                  label={`${capability.label} — allowed ${field}`}
                  discovery={discovery}
                  queryKey={[
                    "capability-input",
                    tenantIntegrationId,
                    capability.key,
                    field,
                    // The form is discovered PER RESOURCE, so the options depend
                    // on which one is asked about. Keyed so switching the allowed
                    // projects re-reads rather than showing the previous one's
                    // issue types.
                    probeResource,
                  ]}
                  load={async (connectionId) => {
                    if (loadCapabilityInputs === undefined) return [];
                    return loadCapabilityInputs(
                      connectionId,
                      capability.key,
                      probeResource,
                      field,
                    );
                  }}
                  value={capabilityInputAllowlist(
                    policy,
                    capability.key,
                    field,
                  )}
                  disabled={!isCapabilityEnabled(policy, capability.key)}
                  onChange={(ids) =>
                    onChange(
                      setCapabilityInputAllowlist(
                        policy,
                        capability.key,
                        field,
                        ids,
                      ),
                    )
                  }
                />
              )}
            </div>
          ));
        })}
      </div>

      <div className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wide text-pb-text-muted">
          Resource restrictions
        </p>
        {product.resourceTypes.map((resourceType) => (
          <div
            key={resourceType.key}
            style={{ marginLeft: `${resourceType.depth * 16}px` }}
          >
            <PolicyAllowlistPicker
              id={`resource-${resourceType.key}`}
              label={resourceType.selectorLabel}
              discovery={discovery}
              queryKey={["resources", tenantIntegrationId, resourceType.key]}
              // Listed over the ADMINISTRATOR'S OWN connection. There is no
              // tenant-wide credential to enumerate a provider with, which is
              // also the limitation worth knowing: an administrator can only
              // allowlist what their own identity can see, and nothing here
              // detects under-allowing.
              load={async (connectionId) =>
                loadResources === undefined
                  ? []
                  : await loadResources(connectionId, resourceType.key)
              }
              value={resourceAllowlist(policy, resourceType.key)}
              disabled={!enabled}
              onChange={(ids) =>
                onChange(setResourceAllowlistIds(policy, resourceType.key, ids))
              }
            />
          </div>
        ))}
      </div>
    </section>
  );
}
